import { Navigate, useLocation, useRoutes, useNavigate, useSearchParams } from 'react-router-dom';
import Login from '../components/Login/login';
import SFT from '../components/SFT/sft';
import ResetPassword from '../components/ResetPassword/ResetPassword';
import { useSelector } from 'react-redux';
import { useProtectedRoutes } from './protected';
import { AuthAppLayout, publicRoutes, SAMLAuthLayout } from './public';
import { useEffect } from 'react';
import { useQueryParams } from '../hooks/useQueryParams';
import ZoomJoinLandignPage from '../components/Zoom/ZoomJoinLandignPage';
import Header from '../components/common/header/header';
import ServiceRequest from '../components/apps/ServiceRequest/ServiceRequest';
import CreateMedicationRequest from '../components/apps/MedicationRequest/CreateMedicationRequest';
import AccountSetup from '../components/Admin/IDP/AccountSetup';
import { POST_LOGIN_REDIRECT_KEY } from '../utils/authUtils';

/**
 * When logged in on /login: (1) optional `?redirect_uri=` same-origin URL — e.g. already SAML-authenticated
 * user opens a bookmarked login link with a return path; (2) sessionStorage from pre-IdP flow after SAML
 * completes (wins when callback URL has no redirect_uri); (3) default role-based home.
 */
function LoginRouteElement({ afterLogedInRoute }) {
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
    const [searchParams] = useSearchParams();

    if (!isLoggedIn) {
        return <AuthAppLayout><Login /></AuthAppLayout>;
    }

    const redirectUriParam = searchParams.get('redirect_uri');
    if (redirectUriParam) {
        try {
            const target = new URL(redirectUriParam, window.location.origin);
            if (target.origin === window.location.origin) {
                return <Navigate to={target.pathname + target.search + target.hash} replace />;
            }
        } catch (e) {
            // ignore invalid URL
        }
    }

    const pending = typeof window !== 'undefined' ? sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) : null;
    if (pending) {
        try {
            const target = new URL(pending, window.location.origin);
            if (target.origin === window.location.origin) {
                sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
                return <Navigate to={target.pathname + target.search + target.hash} replace />;
            }
        } catch (e) {
            sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        }
    }
    return <Navigate to={afterLogedInRoute} replace />;
}

const WrapperWithHeaderOnly = ({ children }) => {
    return (
        <div className="outer-wrapper">
            <Header hideBody={true} />
            <div className="body-outer">
                <div className="body-wrapper">
                    {children}
                </div>
            </div>
        </div>
    )
}

export const AppRoutes = () => {
    let queryParams = useQueryParams();
    let navigate = useNavigate();
    const { pathname } = useLocation();
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
    const user = useSelector((state) => state?.auth?.user);
    const afterLogedInRoute = user?.['cognito:groups']?.includes('Device')
        ? '/app/join-waiting-room-meet'
        : user?.permissions?.[0] === 'Manage Provider Schedule & Appointments'
            ? '/app/providers-schedule-appointments'
            : user?.['cognito:groups']?.includes('Practitioner') || user?.['cognito:groups']?.includes('Super-Admin')
                ? '/app/dashboard'
                : '/login';

    const commonRoutes = [
        { path: '/app/zoom-meeting-join', element: queryParams.get('topic') && queryParams.get('password') ? <ZoomJoinLandignPage topic={queryParams.get('topic')} pass={queryParams.get('password')} /> : <h1>Do not have any seesion details!!</h1> },
        {
            path: '/app/encounter-medication-request', element: <WrapperWithHeaderOnly><CreateMedicationRequest /></WrapperWithHeaderOnly>
        },
        { path: '/', element: isLoggedIn ? <Navigate to={afterLogedInRoute} /> : <Navigate to='/login' /> },
        { path: '/login', element: <LoginRouteElement afterLogedInRoute={afterLogedInRoute} /> },
        { path: '/sft', element: <AuthAppLayout> <SFT /></AuthAppLayout> },
        { path: '/ResetPassword', element: <AuthAppLayout> <ResetPassword /> </AuthAppLayout> },
        { path: `/saml/account-setup`, element: <SAMLAuthLayout><AccountSetup /></SAMLAuthLayout>}
        // { path: '/*', element: <Navigate to='/app/dashboard' /> },
    ];
    const { protectedRoutes } = useProtectedRoutes();
    const routes = isLoggedIn ? protectedRoutes : publicRoutes;
    const element = useRoutes([...routes, ...commonRoutes]);
    const publicPathNames = ['/', '/login', '/auth/setpassword', '/auth/verifyMFA', '/auth/forgotpassword', '/auth/resetpassword', '/app/zoom-meeting-join', '/sft', '/ResetPassword', '/saml/account-setup'];
    useEffect(() => {
        if (!isLoggedIn && !publicPathNames.includes(pathname)) {
            // <Navigate to='/login' />;
            navigate('/login');
        }
    }, [isLoggedIn, pathname]);
    return <>{element}</>;
};
